/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Car,
  Check,
  ChevronRight,
  Clock,
  MapPin,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { carpoolsApi, DAY_LABELS } from "./api";
import "./CarpoolDetail.css";

function RequestRow({ req, isDriver, onAction }) {
  const [loading, setLoading] = useState(false);

  const act = async (action) => {
    setLoading(true);
    try {
      await onAction(req.id, action);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cpd-req-row">
      <div className="cpd-req-info">
        <strong>{req.rider.name}</strong>
        <span className="cpd-req-seats">{req.seats_requested} seat{req.seats_requested > 1 ? "s" : ""}</span>
        <span className={`cpd-req-badge cpd-req-${req.status.toLowerCase()}`}>
          {req.status}
        </span>
      </div>
      {isDriver && req.status === "PENDING" && (
        <div className="cpd-req-actions">
          <button
            className="cpd-btn-accept"
            disabled={loading}
            onClick={() => act("accept")}
          >
            <Check size={14} /> Accept
          </button>
          <button
            className="cpd-btn-decline"
            disabled={loading}
            onClick={() => act("decline")}
          >
            <X size={14} /> Decline
          </button>
        </div>
      )}
      {!isDriver && req.status === "PENDING" && (
        <button
          className="cpd-btn-cancel"
          disabled={loading}
          onClick={() => act("cancel")}
        >
          Cancel
        </button>
      )}
    </div>
  );
}

export default function CarpoolDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ride, setRide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [joining, setJoining] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await carpoolsApi.get(id);
      setRide(data);
    } catch (e) {
      setError(e.message || "Could not load ride");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const handleJoin = async () => {
    setJoining(true);
    try {
      await carpoolsApi.requestToJoin(id, { seats_requested: 1 });
      showToast("Request sent to driver!");
      load();
    } catch (e) {
      showToast(e.message || "Could not send request");
    } finally {
      setJoining(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel this ride? Accepted riders will be notified.")) return;
    setCancelling(true);
    try {
      await carpoolsApi.cancel(id);
      navigate("/carpools");
    } catch (e) {
      showToast(e.message || "Could not cancel");
    } finally {
      setCancelling(false);
    }
  };

  const handleRequestAction = async (reqId, action) => {
    try {
      await carpoolsApi.patchRequest(reqId, action);
      showToast(
        action === "accept"
          ? "Request accepted!"
          : action === "decline"
          ? "Request declined"
          : "Request cancelled"
      );
      load();
    } catch (e) {
      showToast(e.message || "Could not update request");
    }
  };

  if (loading) return <div className="cpd-loading">Loading…</div>;
  if (error) return <div className="cpd-error">{error}</div>;
  if (!ride) return null;

  const isDriver = ride.driver && !ride.requests; // requests only present for driver
  const isOwner = !!ride.requests; // backend sends requests[] only to driver
  const canJoin =
    ride.status === "ACTIVE" && !isOwner && !ride.my_request && ride.seats_available > 0;

  return (
    <div className="cpd-page">
      {toast && <div className="cpd-toast">{toast}</div>}
      <div className="cpd-back-bar">
        <button className="cpd-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} /> Back
        </button>
        {isOwner && ride.status === "ACTIVE" && (
          <button
            className="cpd-btn-danger"
            onClick={handleCancel}
            disabled={cancelling}
          >
            <Trash2 size={14} /> Cancel ride
          </button>
        )}
      </div>

      <div className="cpd-card">
        <div className="cpd-route-row">
          <MapPin size={16} className="cpd-icon" />
          <span className="cpd-origin">{ride.origin_label}</span>
          <ChevronRight size={14} className="cpd-arrow" />
          <span className="cpd-dest">{ride.destination_label}</span>
        </div>
        <div className="cpd-meta-grid">
          <div className="cpd-meta-item">
            <span className="cpd-meta-label">Driver</span>
            <span>{ride.driver.name}</span>
          </div>
          <div className="cpd-meta-item">
            <span className="cpd-meta-label">Departure</span>
            <span>
              <Clock size={13} /> {ride.departure_time}
            </span>
          </div>
          <div className="cpd-meta-item">
            <span className="cpd-meta-label">Seats</span>
            <span>
              <Users size={13} /> {ride.seats_available} available / {ride.seats_total}
            </span>
          </div>
          {ride.vehicle_desc && (
            <div className="cpd-meta-item">
              <span className="cpd-meta-label">Vehicle</span>
              <span>
                <Car size={13} /> {ride.vehicle_desc}
              </span>
            </div>
          )}
        </div>
        <div className="cpd-days">
          {(ride.departure_days || []).map((d) => (
            <span key={d} className="cpd-day-pill">
              {DAY_LABELS[d] || d}
            </span>
          ))}
        </div>
        {ride.notes && <p className="cpd-notes">{ride.notes}</p>}
        {ride.status === "CANCELLED" && (
          <div className="cpd-cancelled-banner">This ride has been cancelled</div>
        )}
      </div>

      {canJoin && (
        <button className="cpd-join-btn" onClick={handleJoin} disabled={joining}>
          {joining ? "Sending…" : "Request to join"}
        </button>
      )}
      {ride.my_request && (
        <div className="cpd-my-req">
          <strong>Your request:</strong>{" "}
          <span
            className={`cpd-req-badge cpd-req-${ride.my_request.status.toLowerCase()}`}
          >
            {ride.my_request.status}
          </span>
          {ride.my_request.status === "PENDING" && (
            <button
              className="cpd-btn-ghost-sm"
              onClick={() => handleRequestAction(ride.my_request.id, "cancel")}
            >
              Cancel request
            </button>
          )}
        </div>
      )}

      {isOwner && ride.requests && ride.requests.length > 0 && (
        <div className="cpd-requests-section">
          <h3>Requests ({ride.requests.length})</h3>
          {ride.requests.map((req) => (
            <RequestRow
              key={req.id}
              req={req}
              isDriver={isOwner}
              onAction={handleRequestAction}
            />
          ))}
        </div>
      )}
      {isOwner && ride.requests && ride.requests.length === 0 && (
        <p className="cpd-no-reqs">No requests yet</p>
      )}
    </div>
  );
}
